import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle, Calculator, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { IMAGES, COMPANY } from "../lib/constants";

const FAQ_GROUPS = [
  {
    id: "detrazioni",
    label: "Detrazioni e Incentivi",
    items: [
      {
        q: "Quali sono oggi le detrazioni fiscali per il fotovoltaico residenziale?",
        a: "Per le abitazioni private è disponibile la detrazione IRPEF del 50% sulla spesa sostenuta per l'installazione dell'impianto fotovoltaico e dell'eventuale sistema di accumulo, da recuperare in 10 quote annuali di pari importo. La detrazione si applica fino a un tetto di spesa di 96.000 € per unità immobiliare. Verifica sempre l'aliquota in vigore al momento dell'installazione: le aliquote sono soggette a revisione con le Leggi di Bilancio.",
      },
      {
        q: "Cos'è il Conto Termico e quando conviene?",
        a: "Il Conto Termico è un incentivo gestito dal GSE che finanzia interventi di efficientamento energetico e impianti a fonti rinnovabili termiche (es. pompe di calore, solare termico). Per il fotovoltaico puro non è generalmente l'opzione principale: la via più frequente per il residenziale resta la detrazione IRPEF. In caso di interventi misti, valuteremo la combinazione migliore in fase di sopralluogo.",
      },
      {
        q: "Esiste ancora lo 'Scambio sul Posto'?",
        a: "Lo Scambio sul Posto (SSP) gestito dal GSE è stato superato dal nuovo meccanismo di valorizzazione dell'energia immessa in rete. Oggi l'energia prodotta e non autoconsumata viene venduta al gestore di rete (Ritiro Dedicato o cessione totale) oppure valorizzata tramite le Comunità Energetiche Rinnovabili (CER). Ti aiutiamo a scegliere lo schema più conveniente per il tuo profilo di consumi.",
      },
      {
        q: "Posso accedere alle Comunità Energetiche Rinnovabili (CER)?",
        a: "Sì. Le CER permettono a famiglie, aziende ed enti locali di condividere l'energia prodotta da impianti rinnovabili in una determinata area, ottenendo incentivi sull'energia condivisa. Se il tuo impianto si trova in una zona idonea ti supportiamo nella verifica e nell'adesione a una CER esistente o nella creazione di una nuova.",
      },
      {
        q: "Le aziende possono beneficiare di agevolazioni specifiche?",
        a: "Sì. Per le imprese sono attive misure come il super-ammortamento / iperammortamento sui beni strumentali, il credito d'imposta per investimenti in beni 4.0 e, a seconda del territorio, bandi regionali dedicati alla transizione energetica. Per impianti industriali di taglia rilevante studiamo il piano fiscale insieme al tuo commercialista.",
      },
    ],
  },
  {
    id: "tecnico",
    label: "Aspetti Tecnici",
    items: [
      {
        q: "Quanto produce mediamente un impianto in Lombardia?",
        a: "In Lombardia un impianto fotovoltaico ben esposto produce mediamente 1.050–1.200 kWh/anno per ogni kWp installato. Su un impianto residenziale tipico da 6 kWp si parla quindi di 6.500–7.000 kWh annui. La produzione effettiva dipende da esposizione, inclinazione, ombreggiamenti e qualità dei componenti.",
      },
      {
        q: "Quanti pannelli servono per coprire i miei consumi?",
        a: "In media servono 1 kWp ogni 1.000 kWh di consumo annuo, corrispondenti a circa 2 pannelli moderni da 450–500 W (ovvero circa 5 mq di tetto). Per una famiglia che consuma 4.500 kWh/anno il dimensionamento tipico è di 4,5–5 kWp. In fase di sopralluogo simuliamo la copertura reale in base alla tua bolletta.",
      },
      {
        q: "Quanto dura un impianto fotovoltaico?",
        a: "I pannelli hanno una vita utile di 25–30 anni e mantengono almeno l'80% della produzione iniziale dopo 25 anni (garanzia di prestazione lineare). L'inverter ha una vita media di 10–15 anni e generalmente va sostituito una volta nel ciclo di vita dell'impianto. Le strutture di supporto in alluminio anodizzato durano oltre 30 anni.",
      },
      {
        q: "Cosa succede se piove o c'è poca luce?",
        a: "I pannelli producono anche con cielo coperto, sebbene con resa ridotta (10–25% rispetto al pieno sole). La pioggia in realtà aiuta: pulisce naturalmente la superficie. Le condizioni peggiori per la resa sono polvere, smog e neve accumulata: ecco perché il lavaggio professionale periodico fa una differenza misurabile.",
      },
      {
        q: "Posso aggiungere batterie di accumulo a un impianto esistente?",
        a: "Sì, la maggior parte degli impianti recenti è compatibile con sistemi di accumulo retrofit. Le batterie consentono di autoconsumare fino al 70–80% dell'energia prodotta (contro il 30–35% senza accumulo). Valutiamo insieme dimensionamento, tipologia chimica (LFP è ormai lo standard) e ritorno economico.",
      },
    ],
  },
  {
    id: "manutenzione",
    label: "Manutenzione e Lavaggio",
    items: [
      {
        q: "Ogni quanto va lavato un impianto fotovoltaico?",
        a: "In linea generale consigliamo un lavaggio professionale ogni 12–18 mesi. La frequenza aumenta in presenza di smog urbano, polveri agricole, aree industriali, vicinanza ad allevamenti o passaggio di volatili. Sui grandi impianti a terra effettuiamo cicli programmati semestrali.",
      },
      {
        q: "Di quanto aumenta la produzione dopo un lavaggio?",
        a: "I dati di campo mostrano che impianti sporchi possono perdere fino al 25% della produzione. Dopo un lavaggio professionale con acqua demineralizzata e spazzole rotanti dedicate, il recupero di resa va tipicamente dal 10% al 25%, ammortizzando il costo del servizio nel giro di pochi mesi.",
      },
      {
        q: "Cosa include un contratto di manutenzione programmata?",
        a: "Il nostro contratto annuale comprende: verifica visiva e termografica dei pannelli, controllo dell'inverter e dei dispositivi di protezione, analisi dei dati di produzione, pulizia delle stringhe e dei quadri elettrici, report tecnico finale con eventuali criticità rilevate. Su richiesta includiamo anche il lavaggio dei pannelli.",
      },
      {
        q: "Se il mio impianto produce poco, intervenite anche se non l'avete installato voi?",
        a: "Certo. Eseguiamo diagnostica e riparazioni guasti su impianti di qualsiasi marca o taglia, anche realizzati da altri installatori. Spesso individuiamo cause semplici (stringa scollegata, ottimizzatore guasto, fusibile bruciato) che ripristinano la piena produzione con interventi rapidi.",
      },
    ],
  },
  {
    id: "burocrazia",
    label: "Burocrazia e Tempi",
    items: [
      {
        q: "Devo chiedere permessi al Comune?",
        a: "Per la maggior parte degli impianti residenziali su tetto è sufficiente la Comunicazione Inizio Lavori Asseverata (CILA) o procedura ancora più snella per impianti integrati. Per immobili in centri storici o sottoposti a vincolo paesaggistico serve l'autorizzazione della Soprintendenza. Gestiamo noi tutta la documentazione.",
      },
      {
        q: "Quanto tempo passa tra il preventivo e l'attivazione dell'impianto?",
        a: "Mediamente 6–10 settimane per il residenziale: 1 settimana per il sopralluogo e il progetto, 2–3 settimane per ordine materiali e pratiche, 1–3 giorni di installazione, 3–5 settimane per le pratiche GSE e l'allaccio del distributore. Per gli impianti industriali i tempi si allungano in funzione della taglia.",
      },
      {
        q: "Cosa include il vostro servizio 'chiavi in mano'?",
        a: "Sopralluogo tecnico, progettazione esecutiva, fornitura dei materiali, installazione, collaudo, pratiche di connessione con il distributore (E-Distribuzione/Unareti/altri), pratiche GSE (Ritiro Dedicato), comunicazione ENEA per la detrazione fiscale, supporto post-vendita e monitoraggio.",
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div data-testid="page-faq">
      <SEO
        title="FAQ Fotovoltaico — Detrazioni, Incentivi, Manutenzione"
        description="Risposte chiare sulle detrazioni 50%, Conto Energia, Comunità Energetiche, dimensionamento impianto, lavaggio pannelli e tempi di installazione. Impresa Edile Lici - Seveso (MB)."
        path="/faq"
        image={IMAGES.residential}
      />

      {/* HERO */}
      <section className="relative h-[45vh] min-h-[340px] flex items-end overflow-hidden">
        <img src={IMAGES.residential} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-14 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-line" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#FED7AA]">Domande & Risposte</span>
          </div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-tight max-w-3xl">
            Detrazioni, tecnica e <span className="italic text-[#FED7AA]">incentivi</span>: tutto chiaro.
          </h1>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="sticky top-28">
              <SectionHeading
                eyebrow="FAQ"
                title="Le domande più frequenti"
                description="Una guida sintetica per orientarsi tra incentivi, scelte tecniche e manutenzione del tuo impianto fotovoltaico."
              />
              <div className="mt-8 space-y-3">
                {FAQ_GROUPS.map((g) => (
                  <a
                    key={g.id}
                    href={`#${g.id}`}
                    data-testid={`faq-anchor-${g.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white hover:border-[#0F5132] hover:shadow-sm transition-all group"
                  >
                    <HelpCircle className="h-4 w-4 text-[#0F5132]" />
                    <span className="text-sm text-[#111827] group-hover:text-[#0F5132]">
                      {g.label}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#4B5563] ml-auto group-hover:translate-x-0.5 transition-transform" />
                  </a>
                ))}
              </div>

              <div className="mt-10 p-6 rounded-2xl bg-[#0F5132] text-white">
                <Calculator className="h-7 w-7 text-[#FED7AA] mb-3" />
                <h4 className="font-serif text-lg">Non trovi la tua risposta?</h4>
                <p className="mt-2 text-sm text-white/85">
                  Chiamaci o scrivici: un nostro tecnico ti risponderà personalmente.
                </p>
                <a
                  href={COMPANY.phoneHref}
                  data-testid="faq-call-cta"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D97706] text-white text-sm font-medium hover:bg-[#B45309] transition-colors"
                >
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-12">
            {FAQ_GROUPS.map((g) => (
              <div key={g.id} id={g.id} className="scroll-mt-28">
                <div className="flex items-center gap-3 mb-6">
                  <span className="accent-line" />
                  <h3 className="font-serif text-2xl text-[#0F5132]">{g.label}</h3>
                </div>
                <Accordion
                  type="single"
                  collapsible
                  className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden"
                  data-testid={`faq-accordion-${g.id}`}
                >
                  {g.items.map((it, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`${g.id}-${idx}`}
                      className="border-b last:border-b-0 border-[#E5E7EB]"
                    >
                      <AccordionTrigger
                        data-testid={`faq-question-${g.id}-${idx}`}
                        className="px-6 py-5 text-base text-[#111827] hover:no-underline hover:bg-[#FAFAFA] transition-colors font-medium"
                      >
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-5 text-[#4B5563] leading-relaxed text-[15px]">
                        {it.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F5132]">
            Pronto a fare i conti con la tua bolletta?
          </h2>
          <p className="mt-4 text-[#4B5563]">
            Richiedi un sopralluogo gratuito e una stima personalizzata del risparmio.
          </p>
          <Link
            to="/contatti"
            data-testid="faq-bottom-cta"
            className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all"
          >
            Richiedi preventivo gratuito <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
